import { Fragment, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  HomeIcon,
  ChatIcon,
  CubeIcon,
  SwitchHorizontalIcon,
  AdjustmentsIcon,
  CalendarIcon,
  DocumentTextIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import Cookies from 'js-cookie';
import {
  LISTURL,
  TITLE,
  COOKIENAME,
  BASEURL,
  BASEREVIEW,
  BASEQUESTION,
} from '../config';
import { authAtom } from '../atoms/auth';
import { useAtomValue, useSetAtom } from 'jotai';
import { Menu, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import { useImmer } from 'use-immer';
import _ from 'lodash';

enum LineActive {
  reviews,
  questions,
}

const Navigation = (): JSX.Element => {
  const auth = useAtomValue(authAtom);
  const logout = useSetAtom(authAtom);
  const handleLogout = () => {
    Cookies.remove(COOKIENAME);
    logout(null);
  };
  const [lineActive, setLineActive] = useImmer<LineActive | null>(null);
  const location = useLocation();
  useEffect(() => {
    if (_.startsWith(location.pathname, `/${BASEURL}/${BASEREVIEW}`)) {
      setLineActive(LineActive.reviews);
    } else if (_.startsWith(location.pathname, `/${BASEURL}/${BASEQUESTION}`)) {
      setLineActive(LineActive.questions);
    } else {
      setLineActive(null);
    }
  }, [location.pathname]);
  return (
    <div className="w-full h-screen flex flex-col navigation-wrap">
      <div className="w-full flex items-center p-3 bg-white h-[60px] shadow-sm relative logo">
        <Link to={LISTURL.general} className="max-w-[140px]">
          <img
            src="https://assets.onistudio.net/review-land-official.png"
            alt={TITLE}
          />
        </Link>
      </div>
      <div className="w-full flex flex-col justify-between navigation-list">
        <ul className="w-full flex flex-col p-3 space-y-2">
          <li className="block">
            <NavLink
              to={LISTURL.general}
              className={({ isActive }) =>
                clsx(
                  'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                  isActive
                    ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                    : undefined,
                )
              }
            >
              <HomeIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Tổng quan</span>
            </NavLink>
          </li>
          <li className="block">
            <Link
              to={LISTURL.reviews_list}
              className={clsx(
                'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                lineActive === LineActive.reviews
                  ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                  : undefined,
              )}
            >
              <ChatIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Đánh giá</span>
            </Link>
            <ul
              className={clsx(
                'flex-col pl-6',
                lineActive === LineActive.reviews ? 'flex' : 'hidden',
              )}
            >
              <li className="w-full">
                <NavLink
                  to={LISTURL.reviews_list}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:text-gray-700',
                      isActive
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : undefined,
                    )
                  }
                >
                  <span className="ml-1 mt-1">Danh sách đánh giá</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink
                  to={LISTURL.reviews_reply}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:text-gray-700',
                      isActive
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : undefined,
                    )
                  }
                >
                  <span className="ml-1 mt-1">Trả lời đánh giá</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink
                  to={LISTURL.reviews_import}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:text-gray-700',
                      isActive
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : undefined,
                    )
                  }
                >
                  <span className="ml-1 mt-1">Import đánh giá</span>
                </NavLink>
              </li>
            </ul>
          </li>
          <li className="block">
            <Link
              to={LISTURL.question_list}
              className={clsx(
                'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                lineActive === LineActive.questions
                  ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                  : undefined,
              )}
            >
              <CalendarIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Câu hỏi thường gặp</span>
            </Link>
            <ul
              className={clsx(
                'flex-col pl-6',
                lineActive === LineActive.questions ? 'flex' : 'hidden',
              )}
            >
              <li className="w-full">
                <NavLink
                  to={LISTURL.question_list}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:text-gray-700',
                      isActive
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : undefined,
                    )
                  }
                >
                  <span className="ml-1 mt-1">Danh sách câu hỏi</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink
                  to={LISTURL.question_reply}
                  className={({ isActive }) =>
                    clsx(
                      'w-full flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:text-gray-700',
                      isActive
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : undefined,
                    )
                  }
                >
                  <span className="ml-1 mt-1">Trả lời câu hỏi</span>
                </NavLink>
              </li>
            </ul>
          </li>
          <li className="block">
            <NavLink
              to={LISTURL.customers}
              className={({ isActive }) =>
                clsx(
                  'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                  isActive
                    ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                    : undefined,
                )
              }
            >
              <UserGroupIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Khách hàng đánh giá</span>
            </NavLink>
          </li>
          <li className="block">
            <NavLink
              to={LISTURL.settings}
              className={({ isActive }) =>
                clsx(
                  'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                  isActive
                    ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                    : undefined,
                )
              }
            >
              <AdjustmentsIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Cấu hình</span>
            </NavLink>
          </li>
          <li className="block">
            <NavLink
              to={LISTURL.documentation}
              className={({ isActive }) =>
                clsx(
                  'w-full flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-700',
                  isActive
                    ? 'bg-gray-200 text-indigo-600 hover:text-indigo-700'
                    : undefined,
                )
              }
            >
              <DocumentTextIcon className="w-6 h-6" />
              <span className="ml-1 mt-1">Hướng dẫn cài đặt</span>
            </NavLink>
          </li>
        </ul>
        <div className="p-3 navigation-shop">
          <div className="text-xs text-left bg-amber-50 text-gray-500 rounded-md p-4 mb-2">
            <p>Nếu cần hỗ trợ gắn code dùng thử, vận hành sử dụng app</p>
            <p>vui lòng liên hệ:</p>
            <p>Zalo: 0921290868</p>
            <p>Email: support@onistudio.net</p>
          </div>
          <div className="text-xs text-center bg-white text-gray-700 rounded-md text-center p-2 mb-2">
            Còn lại&nbsp;
            <span className="text-green-600 font-medium">
              {dayjs(auth?.expired_at).diff(dayjs(), 'day') + 1}
            </span>
            &nbsp;ngày sử dụng
          </div>
          <Menu as="div" className="relative w-full inline-block text-left">
            <Menu.Button className="flex flex-wrap w-full items-center bg-white text-gray-300 px-2 py-4 rounded-md focus:outline-none">
              <div className="w-1/6 inline-flex justify-center">
                <CubeIcon className="w-5 h-5" />
              </div>
              <div className="w-5/6">
                <div className="font-medium text-gray-600 text-sm text-xs text-left">
                  {auth?.myharavan_domain
                    .replace('https://', '')
                    .replace('http://', '')}
                </div>
              </div>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 bottom-full mb-2 w-full origin-bottom-left divide-y divide-gray-100 rounded-md bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="px-1 py-1">
                  <Menu.Item>
                    <button
                      type="button"
                      className="text-gray-700 group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-gray-100 hover:text-gray-900"
                      onClick={handleLogout}
                    >
                      <SwitchHorizontalIcon className="mr-2 h-5 w-5 text-gray-400" />
                      Đổi cửa hàng
                    </button>
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default Navigation;
